import { issueLinks } from '../autolinks.js'
import { onDismiss } from '../dismiss.js'
import { icon, iconSvg } from '../icons.js'
import { keyBindingsView } from '../keyBindings.js'
import m from '../m.js'

export function avatarView({ user, size = 20 }) {
  return (
    user &&
    m('img.avatar', {
      src: `${user.avatarUrl}${user.avatarUrl?.includes('?') ? '&' : '?'}s=${size * 2}`,
      width: size,
      height: size,
      alt: user.login,
      title: user.login,
    })
  )
}

/** Readable text color for a GitHub label hex color. */
function contrast(hex) {
  const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luma > 0.6 ? '#1f2328' : '#fff'
}

export function labelView({ label }) {
  return m.m(
    'span.label',
    { vars: { background: `#${label.color}`, color: contrast(label.color) } },
    label.name
  )
}

/** Small colored dot for a label, as in GitHub's label menus. */
export function labelDotView({ color }) {
  return m.m('span.label-dot', { vars: { background: `#${color}` } })
}

// Our upload URLs: /u/ + 24 char random name. The host can be an older PUBLIC_URL (e.g. a past tunnel).
const UPLOAD = /^https?:\/\/[^/]+(\/u\/[\w-]{24}\.(png|jpg|gif|webp|avif))$/

/**
 * GitHub-rendered (sanitized) HTML. Images from our upload store are loaded from
 * this server directly instead of through GitHub's camo proxy, which cannot reach
 * a local server, or a host the board was reachable at before.
 */
function fixup({ dom }) {
  for (const img of dom.querySelectorAll('img[data-canonical-src]')) {
    const path = img.getAttribute('data-canonical-src').match(UPLOAD)?.[1]
    if (path && img.src !== location.origin + path)
      img.src = location.origin + path
  }
  issueLinks(dom)
  for (const a of dom.querySelectorAll('a[href^="http"]')) a.target = '_blank'
}

/** A "create issue" button after every open task list item; `data-task` is its index among all task items. */
function taskButtons(dom) {
  dom.querySelectorAll('li input[type=checkbox]').forEach((box, i) => {
    const li = box.closest('li')
    if (
      box.checked ||
      li.querySelector(':scope > .task-issue, :scope > p > .task-issue')
    )
      return
    const button = document.createElement('button')
    Object.assign(button, {
      type: 'button',
      className: 'task-issue icon-button',
      title: m.t('task.createIssue'),
      innerHTML: `<span class="octicon">${iconSvg('issue-opened')}</span>`,
    })
    button.setAttribute('aria-label', m.t('task.createIssue'))
    button.dataset.task = i
    // after the item's own text, before a nested list
    const text = box.parentElement
    text.insertBefore(
      button,
      [...text.children].find(el => el.matches('ul, ol')) || null
    )
  })
}

/** `ontask(index, text)`: offers to turn open task list items into issues (see taskButtons). */
export function htmlView({
  html,
  empty = m.t('detail.noDescription'),
  ontask,
}) {
  return m.m(
    '.markdown-body',
    {
      onRender: [fixup, ontask ? ({ dom }) => taskButtons(dom) : null],
      onclick: evt => {
        const button = ontask && evt.target.closest('.task-issue')
        if (!button) {
          evt.redraw = false
          return
        }
        button.disabled = true
        // the item's own text, without nested lists, to match it in the markdown
        const li = button.closest('li').cloneNode(true)
        li.querySelectorAll('ul, ol, .task-issue').forEach(el => el.remove())
        Promise.resolve(
          ontask(Number(button.dataset.task), li.textContent.trim())
        ).finally(() => (button.disabled = false))
      },
    },
    html ? m.trust(html) : m('p.muted', empty)
  )
}

/** An option's content; `view` is a function so every render gets fresh vnodes (Mithril cannot render one vnode twice). */
export function optionView(opt) {
  return opt.view ? opt.view() : opt.label
}

/**
 * Button that stays busy until the promise of its `onclick` settles, after webapp's
 * asyncButton. Errors are not handled here: the store shows them as toasts.
 */
export const asyncButtonView = m.withState(() => {
  let busy = false
  return (
    {
      onclick: onClick,
      disabledReason = [],
      busyReason = m.t('button.busy'),
      ...attrs
    },
    content,
    busyContent = content
  ) =>
    m.m(
      'button',
      {
        type: 'button',
        ...attrs,
        cls: { ...attrs.cls, busy },
        disabledReason: [[busy, busyReason], ...disabledReason],
        onclick: async evt => {
          busy = true
          try {
            await onClick(evt)
          } catch {
            // the store shows errors as toasts
          } finally {
            busy = false
            m.redraw()
          }
        },
      },
      busy ? busyContent : content
    )
})

/**
 * A handle with a menu, after webapp's dropOpen. The menu is a popover in the top layer,
 * anchored to the handle (see anchored()); a click outside or Esc closes it (see dismiss.js).
 * `handleView(isOpen, anchor)`: spread `anchor` on the element the menu is placed at,
 * `contentView(close)`: the menu, `closeOnContentClick`: whether a click in it closes it,
 * `anchorName`: an anchor-name set elsewhere to place the menu at instead.
 */
export const dropOpen = m.withState(initial => {
  const { anchor, popover } = anchored(initial.anchorName)
  let isOpen = false
  let root
  let menu
  let attrs
  let undismiss = null
  function openMenu() {
    isOpen = true
    undismiss = onDismiss({
      contains: target => root.contains(target),
      close: closeMenu,
    })
    attrs.onOpen?.()
  }
  function closeMenu() {
    if (!isOpen) return
    isOpen = false
    undismiss?.()
    undismiss = null
    attrs.onClose?.()
    m.redraw()
  }
  return current => {
    attrs = current
    const {
      tagName = '.drop-open',
      handleView,
      contentView,
      closeOnContentClick = true,
      align = 'right',
      menuClass,
      cls,
      onOpen,
      onClose,
      anchorName,
      ...rest
    } = attrs
    return m.m(
      tagName,
      {
        ...rest,
        cls: { 'is-open': isOpen, ...cls },
        onRender: ({ dom }) => (root = dom),
        onclick: evt => {
          if (evt.target === root) return
          // the menu can have closed itself already (e.g. a picked option)
          if (!menu?.contains(evt.target)) isOpen ? closeMenu() : openMenu()
          else if (closeOnContentClick) closeMenu()
        },
      },
      [
        m.onRemove(() => undismiss?.()),
        handleView(isOpen, anchor),
        isOpen &&
          m.m(
            '.picker-menu',
            {
              ...popover,
              cls: { [`align-${align}`]: true, [menuClass]: menuClass },
              onRender: ({ dom }) => (menu = dom),
            },
            contentView(closeMenu)
          ),
      ]
    )
  }
})

/**
 * GitHub-style select menu. `options`: [{ value, label, view?: () => vnodes }], `selected`: values.
 * A click applies right away and closes; in a multi-select, Shift-click toggles and
 * keeps it open, and Apply (or closing it) reports `onchange(values)`. `toggle` is the button content,
 * `anchorName` an anchor-name set elsewhere to place the menu at (default: the toggle).
 */
let pickers = 0

export const pickerView = m.withState(() => {
  const scope = `picker-${++pickers}`
  let first // option Enter in the filter picks; the bindings are set once, so not `shown[0]`
  let draft = []
  let query = ''
  let attrs
  // a plain click applies right away; with Shift held a multi-select stays open for more
  function pick(value, evt, closeMenu) {
    if (attrs.multi === false) {
      draft = draft.includes(value) ? [] : [value]
      return closeMenu()
    }
    draft = draft.includes(value)
      ? draft.filter(v => v !== value)
      : [...draft, value]
    if (!evt?.shiftKey) closeMenu()
  }
  return current => {
    attrs = current
    const {
      toggle,
      heading,
      options,
      selected,
      disabled,
      align = 'right',
      className,
      anchorName,
    } = attrs
    const q = query.trim().toLowerCase()
    const shown = q
      ? options.filter(o =>
          String(o.label ?? o.value)
            .toLowerCase()
            .includes(q)
        )
      : options
    first = shown[0]
    return dropOpen({
      tagName: '.picker',
      className,
      anchorName,
      align,
      closeOnContentClick: false,
      onOpen: () => {
        query = ''
        draft = [...selected]
      },
      onClose: () => {
        if (
          draft.slice().sort().join('\n') !==
          [...attrs.selected].sort().join('\n')
        )
          attrs.onchange(draft)
      },
      handleView: (isOpen, anchor) =>
        m(
          'button.picker-toggle',
          {
            ...anchor,
            type: 'button',
            title: attrs.title || heading,
            'aria-label': attrs.title || heading,
            disabled,
          },
          toggle
        ),
      contentView: closeMenu => [
        heading && m('.picker-heading', heading),
        options.length > 8 &&
          m(
            '.picker-filter',
            m('input', {
              placeholder: m.t('menu.filter'),
              value: query,
              oncreate: ({ dom }) => dom.focus(),
              oninput: evt => (query = evt.target.value),
            }),
            keyBindingsView(
              scope,
              { enter: evt => first && pick(first.value, evt, closeMenu) },
              { skipInInputs: false }
            )
          ),
        m(
          '.picker-options',
          shown.length === 0
            ? m('.picker-empty', m.t('menu.empty'))
            : shown.map(opt =>
                m(
                  'button.picker-option',
                  {
                    type: 'button',
                    onclick: evt => pick(opt.value, evt, closeMenu),
                  },
                  [
                    m(
                      'span.picker-check',
                      draft.includes(opt.value) && icon('check')
                    ),
                    optionView(opt),
                  ]
                )
              )
        ),
        attrs.multi !== false &&
          m('.picker-footer', [
            m('span.muted', m.t('menu.shiftHint')),
            m(
              'button.primary.small',
              {
                type: 'button',
                title: m.t('menu.applyHint'),
                onclick: closeMenu,
              },
              m.t('menu.apply')
            ),
          ]),
      ],
    })
  }
})

let anchors = 0

/**
 * Attributes for a toggle and its menu: the menu is a popover in the top layer (no
 * clipping by scrolling lanes), placed with CSS anchor positioning; the browser
 * flips it to wherever it fits (see .picker-menu).
 */
export function anchored(anchorName) {
  const name = anchorName || `--menu-${++anchors}`
  return {
    anchor: anchorName ? {} : { style: `anchor-name: ${name}` },
    popover: {
      popover: 'manual',
      style: `position-anchor: ${name}`,
      oncreate: ({ dom }) => dom.showPopover(),
    },
  }
}
